<!-- eslint-disable vue/valid-template-root -->
<template>
  <assignment-type-modal
    :id="session.workspace.id"
    :show-modal="showAssignmentTypeModal"
    @close="showAssignmentTypeModal = false"
  />

  <create-calibration-session
    :show="showCalibrationModal"
    :edit="edit"
    :active="edit?.sessionId === calibrationSessionId"
    @close="closeCalibrationModal"
    @update="getCalibrationSessions"
  />
</template>
<!-- eslint-enable vue/valid-template-root -->

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { type AssignmentV2_UserAssignment } from '@zendesk/zqa-services/assignments_v2'
import { type SubnavActionMenuItem } from '@klausapp/app-navigation/types'

import i18n from '@/i18n'
import { hasAccountRole, hasWorkspaceRole } from '@/utils/roleUtils'
import useTasks from '@/composables/useTasks'
import { type CalibrationSession } from '@/api/calibration'

import { session } from '@/composables/useSession'
import useTicketRouteParams from '@/composables/useTicketRouteParams'
import useAssignmentSection from '@/modules/tasks/composables/useAssignmentSection'
import useCalibrationModal from '@/modules/tasks/composables/useCalibrationModal'
import useCreateAssignmentModal from '@/modules/tasks/composables/useCreateAssignmentModal'
import { useWorkspaceConnections } from '@/modules/workspace/composables/useWorkspaceConnections'
import CreateCalibrationSession from '@/modules/tasks/components/calibrations/CreateCalibrationSession.vue'
import AssignmentTypeModal from '@/modules/workspace/components/AssignmentTypeModal.vue'

import type { SidebarConfig } from '../types'

const emit = defineEmits<{
  'update:config': [config: SidebarConfig]
}>()

const TASKS_NAV_KEYS = {
  PERSONAL: 'personal',
  PERSONAL_REVIEWER_QA: 'personal-reviewer-qa',
  WORKSPACE: 'ws',
  WORKSPACE_REVIEWER_QA: 'ws-reviewer-qa',
  CALIBRATIONS: 'calibrations',
}

const FILTERS = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
}

const TASKS_FILTER_MENU_CONFIG = {
  tooltip: '',
  itemGroups: [
    {
      key: 'filter',
      items: [{ key: 'SHOW_ACTIVE_ONLY', label: i18n.t('tasks.navbar.filter.option.include_completed') }],
    },
  ],
}

const router = useRouter()
const { assignmentId, connectionId, conversationId, calibrationSessionId } = useTicketRouteParams()
const {
  edit,
  showModal: showCalibrationModal,
  closeModal: closeCalibrationModal,
  editCalibrationSession,
  deleteCalibrationSession,
} = useCalibrationModal()
const { showModal: showAssignmentTypeModal } = useCreateAssignmentModal()
const { hasConnections } = useWorkspaceConnections(session.workspace.id)

// add new assignment or calibration session
const handleTitleActionClick = (key?: string) => {
  if (key === 'create_assignment' && session.features.gradeTheReviewerDev) {
    showAssignmentTypeModal.value = true
    return
  }

  if (key === 'create_assignment' && !session.features.gradeTheReviewerDev) {
    return router.push({ name: 'workspace.assignments.agent-qa.new', params: { id: session.workspace.id } })
  }

  if (key === 'create_calibration') {
    showCalibrationModal.value = true
    return
  }
  return
}

// filter items in list
const handleGroupActionClick = (view: string) => {
  sectionFilters[view] = sectionFilters[view] === FILTERS.ALL ? FILTERS.ACTIVE : FILTERS.ALL
}

const handleCalibrationItemActions = (navItem: string, _subnavItem: string | number, action: string) => {
  if (navItem === TASKS_NAV_KEYS.CALIBRATIONS) {
    const session = state.allCalibrationSessions?.find((s) => s.sessionId === _subnavItem)
    if (!session) return

    if (action === 'edit') {
      editCalibrationSession(session)
    } else if (action === 'delete') {
      deleteCalibrationSession(session)
    }
  }
}

const handleSubnavItemClick = (navItem: string, subnavItem: string) => {
  if (navItem === TASKS_NAV_KEYS.PERSONAL || navItem === TASKS_NAV_KEYS.WORKSPACE) {
    router.push({
      name: 'tasks.assignments.review',
      params: {
        assignmentId: encodeURIComponent(subnavItem),
        connectionId: subnavItem === assignmentId.value ? connectionId.value : '',
        conversationId: subnavItem === assignmentId.value ? conversationId.value : '',
      },
    })
  }
  if (navItem === TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA || navItem === TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA) {
    router.push({
      name: 'tasks.assignments.reviewer-qa.review',
      params: {
        assignmentId: encodeURIComponent(subnavItem),
        connectionId: subnavItem === assignmentId.value ? connectionId.value : '',
        conversationId: subnavItem === assignmentId.value ? conversationId.value : '',
      },
    })
  }
  if (navItem === TASKS_NAV_KEYS.CALIBRATIONS) {
    router.push({
      name: 'tasks.calibrations.review',
      params: {
        calibrationSessionId: encodeURIComponent(subnavItem),
        conversationId: subnavItem === calibrationSessionId.value ? conversationId.value : '',
        connectionId: subnavItem === calibrationSessionId.value ? connectionId.value : '',
      },
    })
  }
}

// PERMISSIONS
const canManage = computed(
  () => hasWorkspaceRole('MANAGER', 'LEAD') || (!session.workspace?.role && hasAccountRole('ADMIN', 'MANAGER')),
)

const canSeeCalibrations = computed(() => {
  const hasRequiredRole =
    hasWorkspaceRole('MANAGER', 'LEAD', 'REVIEWER') || (!session.workspace?.role && hasAccountRole('ADMIN', 'MANAGER'))
  const settingEnabled = session.workspace.settings.calibrationEnabled

  return hasRequiredRole && settingEnabled
})

// NAV SECTIONS
const {
  state,
  allAssignments,
  activeAssignmentCycles,
  activeReviewerQAAssignmentCycles,
  activeSessions,
  completedAssignmentCycles,
  completedReviewerQAAssignmentCycles,
  getCalibrationSessions,
} = useTasks()

const isReviewee = (item: AssignmentV2_UserAssignment) => item?.isReviewer
const isNotReviewer = (item: AssignmentV2_UserAssignment) => !item?.isReviewer
const isCompleted = (goal: number, reviewCount: number) => !goal || reviewCount === goal

// active cycles - initial list items in sidenav
const personalActiveCycles = computed(() => activeAssignmentCycles.value.filter(isReviewee))
const personalReviewerQAActiveCycles = computed(() => activeReviewerQAAssignmentCycles.value.filter(isReviewee))
const wsActiveCycles = computed(() => activeAssignmentCycles.value.filter(isNotReviewer))
const wsReviewerQAActiveCycles = computed(() => activeReviewerQAAssignmentCycles.value.filter(isNotReviewer))

// completed cycles
const personalCompletedCycles = computed(() => completedAssignmentCycles.value.filter(isReviewee))
const personalReviewerQACompletedCycles = computed(() => completedReviewerQAAssignmentCycles.value.filter(isReviewee))
const wsCompletedCycles = computed(() => completedAssignmentCycles.value.filter(isNotReviewer))
const wsReviewerQACompletedCycles = computed(() => completedReviewerQAAssignmentCycles.value.filter(isNotReviewer))

// all cycles - list items when filter == completed
const personalCycles = computed(() => [...personalActiveCycles.value, ...personalCompletedCycles.value])
const wsCycles = computed(() => [...wsActiveCycles.value, ...wsCompletedCycles.value])
const personalReviewerQACycles = computed(() => [
  ...personalReviewerQAActiveCycles.value,
  ...personalReviewerQACompletedCycles.value,
])
const wsReviewerQACycles = computed(() => [...wsReviewerQAActiveCycles.value, ...wsReviewerQACompletedCycles.value])

// Filter state management
const sectionFilters = reactive({
  [TASKS_NAV_KEYS.PERSONAL]: FILTERS.ACTIVE,
  [TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA]: FILTERS.ACTIVE,
  [TASKS_NAV_KEYS.WORKSPACE]: FILTERS.ACTIVE,
  [TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA]: FILTERS.ACTIVE,
  [TASKS_NAV_KEYS.CALIBRATIONS]: FILTERS.ACTIVE,
})

const { isCurrentCycle } = useAssignmentSection()
const initialActiveSection = ref()

watch([allAssignments, personalCycles, wsCycles, personalReviewerQACycles, wsReviewerQACycles, calibrationSessionId], () => {
  let section
  if (personalCycles.value.some(isCurrentCycle)) {
    section = TASKS_NAV_KEYS.PERSONAL
  } else if (personalReviewerQACycles.value.some(isCurrentCycle)) {
    section = TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA
  } else if (wsCycles.value.some(isCurrentCycle)) {
    section = TASKS_NAV_KEYS.WORKSPACE
  } else if (wsReviewerQACycles.value.some(isCurrentCycle)) {
    section = TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA
  } else if (calibrationSessionId.value) {
    section = TASKS_NAV_KEYS.CALIBRATIONS
  }
  initialActiveSection.value = section
})

// SIDENAV ITEMS
const toSidenavItem = (assignments: AssignmentV2_UserAssignment[], sectionKey: string) => {
  return assignments?.map(({ name, id, goal, reviewCount }) => {
    return {
      children: name,
      key: id,
      tag: isCompleted(goal, reviewCount) ? undefined : `${reviewCount}/${goal}`,
      isCurrent: assignmentId.value === id,
      dataTestId: `${sectionKey}-assignments-${id}`,
      view: `${sectionKey}`,
    }
  })
}

const calibrationToSidenavItem = (sessions: CalibrationSession[]) => {
  return sessions?.map(({ title, sessionId }) => ({
    children: title,
    key: sessionId,
    isCurrent: calibrationSessionId.value === sessionId,
    dataTestId: `calibration-${sessionId}`,
    view: TASKS_NAV_KEYS.CALIBRATIONS,
    overflowMenu: canManage.value,
    actions: canManage.value
      ? [
          { key: 'edit', tooltip: i18n.t('universal.edit'), dataTestId: 'calibration-edit-btn' },
          { key: 'delete', tooltip: i18n.t('universal.delete'), dataTestId: 'calibration-delete-btn' },
        ]
      : [],
  }))
}

const assignmentsItemsMap = {
  [TASKS_NAV_KEYS.PERSONAL]: {
    active: () => personalActiveCycles.value,
    all: () => personalCycles.value,
  },
  [TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA]: {
    active: () => personalReviewerQAActiveCycles.value,
    all: () => personalReviewerQACycles.value,
  },
  [TASKS_NAV_KEYS.WORKSPACE]: {
    active: () => wsActiveCycles.value,
    all: () => wsCycles.value,
  },
  [TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA]: {
    active: () => wsReviewerQAActiveCycles.value,
    all: () => wsReviewerQACycles.value,
  },
}

const getSectionItems = (sectionKey: string) => {
  const assignmentsMap = assignmentsItemsMap[sectionKey]
  if (!assignmentsMap) return []

  const includeCompletedAssignments = sectionFilters[sectionKey] === FILTERS.ALL
  return includeCompletedAssignments ? assignmentsMap.all() : assignmentsMap.active()
}

const getCalibrationSectionItems = (sectionKey: string) => {
  if (sectionFilters[sectionKey] === FILTERS.ALL) return state?.allCalibrationSessions || []
  return activeSessions.value || []
}

const getNoAssignmentsLabel = (key: string) => {
  if (sectionFilters[key] === FILTERS.ALL) return i18n.t('tasks.no_assignments')
  return i18n.t('tasks.no_active_assignments')
}

const getNoCalibrationSessionsLabel = () => {
  if (sectionFilters[TASKS_NAV_KEYS.CALIBRATIONS] === FILTERS.ALL) return i18n.t('tasks.no_calibration_sessions')
  return i18n.t('tasks.no_active_calibration_sessions')
}

const formatBadgeCount = (count: number) => {
  if (count === 0) return undefined
  return count > 99 ? '99+' : count
}

const items = computed(() => {
  const personalAgentQA = {
    key: TASKS_NAV_KEYS.PERSONAL,
    legend: session.features.gradeTheReviewerDev
      ? i18n.t('tasks.navbar.personal_agent_qa.title')
      : i18n.t('tasks.navbar.personal_assignments.title'),
    isCollapsible: true,
    defaultCollapsed: initialActiveSection.value !== TASKS_NAV_KEYS.PERSONAL,
    isOverflowMenu: true,
    actionMenu: TASKS_FILTER_MENU_CONFIG,
    items: toSidenavItem(getSectionItems(TASKS_NAV_KEYS.PERSONAL), TASKS_NAV_KEYS.PERSONAL),
    dataTestId: 'tasks-personal',
    view: TASKS_NAV_KEYS.PERSONAL,
    emptyStateLabel: getNoAssignmentsLabel(TASKS_NAV_KEYS.PERSONAL),
    badge: formatBadgeCount(personalActiveCycles.value.length),
  }

  const personalReviewerQA = {
    key: TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA,
    legend: i18n.t('tasks.navbar.personal_reviewer_qa.title'),
    isCollapsible: true,
    defaultCollapsed: initialActiveSection.value !== TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA,
    isOverflowMenu: true,
    actionMenu: TASKS_FILTER_MENU_CONFIG,
    items: toSidenavItem(getSectionItems(TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA), TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA),
    dataTestId: 'tasks-personal-reviewer-qa-assignments',
    view: TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA,
    emptyStateLabel: getNoAssignmentsLabel(TASKS_NAV_KEYS.PERSONAL_REVIEWER_QA),
    badge: formatBadgeCount(personalReviewerQAActiveCycles.value.length),
  }

  // Workplace Agent QA
  const agentQA = {
    key: 'ws-agent-qa',
    legend: session.features.gradeTheReviewerDev
      ? i18n.t('tasks.navbar.agent_qa.title')
      : i18n.t('tasks.navbar.workspace_assignments.title'),
    isCollapsible: true,
    defaultCollapsed: initialActiveSection.value !== TASKS_NAV_KEYS.WORKSPACE,
    isOverflowMenu: true,
    actionMenu: TASKS_FILTER_MENU_CONFIG,
    items: toSidenavItem(getSectionItems(TASKS_NAV_KEYS.WORKSPACE), TASKS_NAV_KEYS.WORKSPACE),
    dataTestId: 'tasks-workspace',
    view: TASKS_NAV_KEYS.WORKSPACE,
    emptyStateLabel: getNoAssignmentsLabel(TASKS_NAV_KEYS.WORKSPACE),
  }

  const reviewerQA = {
    key: TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA,
    legend: i18n.t('settings.workspaces.assignments.type.reviewer_qa_label'),
    isCollapsible: true,
    defaultCollapsed: initialActiveSection.value !== TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA,
    isOverflowMenu: true,
    actionMenu: TASKS_FILTER_MENU_CONFIG,
    items: toSidenavItem(getSectionItems(TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA), TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA),
    dataTestId: 'tasks-workspace-reviewer-qa-assignments',
    view: TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA,
    emptyStateLabel: getNoAssignmentsLabel(TASKS_NAV_KEYS.WORKSPACE_REVIEWER_QA),
  }

  const calibrations = {
    key: TASKS_NAV_KEYS.CALIBRATIONS,
    legend: i18n.t('conversations.sub_nav.calibrations'),
    isCollapsible: true,
    defaultCollapsed: !calibrationSessionId.value,
    items: calibrationToSidenavItem(getCalibrationSectionItems(TASKS_NAV_KEYS.CALIBRATIONS)),
    dataTestId: 'tasks-calibration',
    view: TASKS_NAV_KEYS.CALIBRATIONS,
    isOverflowMenu: true,
    actionMenu: TASKS_FILTER_MENU_CONFIG,
    emptyStateLabel: getNoCalibrationSessionsLabel(),
  }

  const conditionalItems = []

  if (session.features.gradeTheReviewerDev && canManage.value) conditionalItems.push(personalReviewerQA)
  if (canManage.value) conditionalItems.push(agentQA)
  if (session.features.gradeTheReviewerDev && canManage.value) conditionalItems.push(reviewerQA)
  if (canSeeCalibrations.value) conditionalItems.push(calibrations)

  return [personalAgentQA, ...conditionalItems]
  // Personal settings group
})

const titleActionItems: SubnavActionMenuItem[] = [
  { key: 'create_assignment', label: i18n.t('tasks.navbar.add_assignment'), dataTestid: 'select-assignment-type-btn' },
  { key: 'create_calibration', label: i18n.t('tasks.navbar.add_calibration'), dataTestid: 'create-calibration-btn' },
]

const actionMenu = computed(() => {
  if ((hasAccountRole('ADMIN', 'MANAGER') || hasWorkspaceRole('MANAGER', 'LEAD')) && hasConnections.value) {
    const menuItems = session.workspace.settings.calibrationEnabled ? titleActionItems : [titleActionItems[0]]
    return {
      tooltip: i18n.t('tasks.plus_button_tooltip'),
      onClick: handleTitleActionClick,
      menuItems,
    }
  }

  return {
    tooltip: '',
    onClick: () => {
      // No-op
    },
    menuItems: [],
  }
})

const config = computed(
  (): SidebarConfig => ({
    title: i18n.t('tasks.title'),
    items: items.value,
    action: actionMenu.value,
    handleSubnavItemClick,
    handleSubnavItemActionClick: handleCalibrationItemActions,
    handleGroupActionClick,
  }),
)
// Emit initial config and watch for changes
onMounted(() => {
  emit('update:config', config.value)
})

// Watch for changes and emit updates
watch(config, (newConfig) => {
  emit('update:config', newConfig)
})
</script>
